import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useGetVehiclesQuery,
  useDeleteVehicleMutation,
  useGetUserCarsQuery,
  useUpdateVehicleMutation
} from "../../service/features/api";
import { SaudiRiyal } from "lucide-react";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { VEHICLE_TYPE } from "../../constants/enum";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal";

import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";

interface VehicleListingsProps {
  type: string;
  searchTerm?: string;
  featured?: boolean;
  page?: number;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  showActions?: boolean;
  userId?: string;
  module?: string;
  showDealerDetail?: boolean;
  showDisableEnable?: boolean;
  showFeatured?: boolean;
  customData?: any[];
}

const typeMapping: Record<string, number> = {
  "سيارة / Car": VEHICLE_TYPE.CAR,
  "دراجة / Bike": VEHICLE_TYPE.BIKE,
  "دراجة كهربائية / E-Bike": VEHICLE_TYPE.BICYCLE,
  "كرفان / Caravan": VEHICLE_TYPE.CARAVAN,
  "معدات ثقيلة / Heavy Equipment": VEHICLE_TYPE.TRUCK,
  "معدات ثقيلة (إيجار) / Heavy Equipment (Rental)": VEHICLE_TYPE.HEAVY_VEHICLE
};


const VehicleListings = ({ 
  type, 
  searchTerm = "", 
  featured = false, 
  page: propsPage, 
  setPage: propsSetPage, 
  showActions = true, 
  userId,
  module = "Listings Management",
  showDealerDetail = true,
  showDisableEnable = true,
  showFeatured = true,
  customData
}: VehicleListingsProps) => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Listings Management", "isEdit");

  const navigate = useNavigate();
  const [internalPage, setInternalPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedVehicle, setSelectedVehicle] = React.useState<any>(null);

  // Use props if provided, otherwise fallback to internal state
  const currentPage = propsPage !== undefined ? propsPage : internalPage;
  const handlePageChange = propsSetPage !== undefined ? propsSetPage : setInternalPage;

  // Reset internal page when search term changes
  React.useEffect(() => {
    if (propsPage === undefined) {
      setInternalPage(1);
    }
  }, [searchTerm, propsPage]);

  // If userId is present, use the user cars query, else use general vehicles query
  const { data: userCarsData, isFetching: isUserCarsFetching } = useGetUserCarsQuery(
    userId ? { id: userId, page: currentPage, limit: 10, search: searchTerm, type: typeMapping[type] } : { id: '', page: 1 } as any,
    { skip: !userId }
  );

  const { data: vehiclesData, isFetching: isVehiclesFetching } = useGetVehiclesQuery(
    {
      search: searchTerm,
      page: currentPage,
      limit: 10,
      type: typeMapping[type]
    },
    { skip: !!userId }
  );

  const isFetching = customData ? false : (userId ? isUserCarsFetching : isVehiclesFetching);
  const data = customData ? { data: { data: customData, paginator: { itemCount: customData.length, pageCount: Math.ceil(customData.length / 10) } } } : (userId ? userCarsData : vehiclesData);

  const [deleteVehicle, { isLoading: isDeleting }] = useDeleteVehicleMutation();
  const [updateVehicle] = useUpdateVehicleMutation();

  const getUpdatePayload = (row: any, updates: any) => {
    const getID = (item: any) => {
      if (!item) return null;
      if (typeof item === 'string') return item;
      return item._id || item.id || null;
    };

    const payload: any = {
      type: row.type,
      sellerType: row.sellerType || 1,
      categoryId: getID(row.categoryId),
      price: Number(row.price),
      name: row.name,
      images: row.images || [],
      isFeatured: row.isFeatured,
      isLuxury: row.isLuxury,
      gcc: row.gcc,
      miles: row.miles,
      modelId: getID(row.modelId),
      brandId: getID(row.brandId),
      location: row.location || { type: "Point", coordinates: [0, 0] },
      address: row.address || { city: "Default City", country: "Default Country" },
      contactInfo: row.contactInfo || { phone: "0000000000", email: "default@example.com" },
      registrationNo: row.registrationNo || "N/A",
      weight: Number(row.weight) || 0,
      isActive: row.isActive,
      ...updates
    };

    const optionalIds = [
      'subCategoryId', 'dealerId', 'variantId', 'axlesId', 'damageTypeId',
      'driveTypeId', 'emissionClassId', 'environmentalBadgeId', 'engineDetailId',
      'transmissionId', 'vehicleTypeId', 'interiorColorId', 'exteriorColorId'
    ];

    optionalIds.forEach(field => {
      const id = getID(row[field]);
      if (id) payload[field] = id;
    });

    if (row.registrationYear) payload.registrationYear = row.registrationYear;
    if (row.manufactureYear) payload.manufactureYear = row.manufactureYear;
    if (row.sortDescription) payload.sortDescription = row.sortDescription;
    if (row.description) payload.description = row.description;
    if (row.videoUrl) payload.videoUrl = row.videoUrl;
    if (row.paymentMethod) payload.paymentMethod = row.paymentMethod;
    if (row.isElectric !== undefined) payload.isElectric = row.isElectric;
    if (row.isAvailable !== undefined) payload.isAvailable = row.isAvailable;

    const features = (row.features || []).map((f: any) => getID(f)).filter(Boolean);
    if (features.length > 0) {
      payload.features = features;
    }

    return payload;
  };

  const handleToggleActive = async (row: any) => {
    try {
      await updateVehicle({ id: row._id, isActive: !row.isActive }).unwrap();
      toast.success(`Vehicle ${!row.isActive ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update vehicle status");
    }
  };

  const handleToggleFeatured = async (row: any) => {
    try {
      await updateVehicle({ id: row._id, isFeatured: !row.isFeatured }).unwrap();
      toast.success(`Vehicle ${!row.isFeatured ? 'featured' : 'unfeatured'} successfully`);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update featured status");
    }
  };

  const allVehicles = customData ? customData : ((data?.data?.data || []).concat(Array.isArray(data?.data) ? data.data : []));

  // For user cars, the API might return just a list without paginator wrapper if structure differs
  // The provided response shows data.data is the array.

  // For general vehicles, we filter by type.
  // For user vehicles, we trusted the API to filter by type (we sent type param), 
  // OR we filter here again to be safe.
  let filteredVehicles = allVehicles.filter((row: any) => row.type === typeMapping[type]);

  // If userId is present, apply client-side search (since API seems to return all or ignore search)
  if (userId && searchTerm) {
    filteredVehicles = filteredVehicles.filter((row: any) =>
      row.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Handle pagination data
  const paginator = data?.data?.paginator || ((customData || userId) ? {
    itemCount: filteredVehicles.length,
    pageCount: Math.ceil(filteredVehicles.length / 10),
  } : undefined);

  // For user cars (client-side pagination), slice the data.
  // For general vehicles (server-side pagination), use as is.
  const displayVehicles = (customData || userId)
    ? filteredVehicles.slice((currentPage - 1) * 10, currentPage * 10)
    : filteredVehicles;

  const handleDeleteClick = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setOpenDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedVehicle?._id) return;
    try {
      await deleteVehicle(selectedVehicle._id).unwrap();
      toast.success("Vehicle deleted successfully");
      setOpenDeleteModal(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  if (isFetching && currentPage === 1 && allVehicles.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>م / Sr. No.</TableCell>
              <TableCell>تفاصيل / {type} Detail</TableCell>
              <TableCell>السعر / Price</TableCell>
              <TableCell>تاريخ الإدراج / Listed Date</TableCell>
              {showDealerDetail && <TableCell>تفاصيل الوكيل/البائع / Dealer/Seller Detail</TableCell>}
              {showDisableEnable && <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>}
              {showFeatured && <TableCell>مميز / Featured</TableCell>}
              {showActions && <TableCell>إجراءات / Actions</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {displayVehicles.map((row: any, index: number) => (
              <TableRow hover key={row._id}>
                <TableCell>{(currentPage - 1) * 10 + index + 1}</TableCell>
                <TableCell>
                  <div className="user_block">
                    <figure className="no_radius">
                      <img
                        src={row.images?.[0] || "/static/images/placeholder.png"}
                        alt={row.name}
                        onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                      />
                    </figure>
                    <div>
                      <p>
                        <b className="flex">
                          <span className="limit">{row.name}</span>
                          {row.condition?.name && (
                            <span
                              className={`status_btn ${row.condition?.name.toLowerCase() === "used" ? "c_dark_warning" : "c_dark_info"
                                }`}
                            >
                              {row.condition?.name}
                            </span>
                          )}
                        </b>
                            {/* {(row.modelId?.name || row.model?.name || row.miles !== undefined) && (
                              <span>{row.model?.name || row.modelId?.name || "N/A"} • {row.miles ? "Miles" : "KM"}</span>
                            )} */}
                      </p>
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <strong className="c_primary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><SaudiRiyal size={16} />{row.price?.toLocaleString() || "0"}</strong>
                </TableCell>

                <TableCell>{moment(row.createdAt).format("DD-MMM-YYYY")}</TableCell>

                {showDealerDetail && (
                  <TableCell>
                    <div className="user_block">
                      <p>
                        <b>{row.dealer?.fullName || row.dealerId?.fullName || row.dealer?.name || row.dealerId?.name || "N/A"}</b>
                        <span>{row.dealer?.countryCode || row.dealerId?.countryCode || ""}{row.dealer?.phone || row.dealerId?.phone || "N/A"}</span>
                      </p>
                    </div>
                  </TableCell>
                )}

                {showDisableEnable && (
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={!!row.isActive}
                        onChange={() => handleToggleActive(row)}
                      />
                    ) : (
                      <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                        {row.isActive ? "Enabled" : "Disabled"}
                      </span>
                    )}
                  </TableCell>
                )}

                {showFeatured && (
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={!!row.isFeatured}
                        onChange={() => handleToggleFeatured(row)}
                      />
                    ) : (
                      <span className={`status_btn ${row.isFeatured ? "c_dark_info" : "c_dark_warning"}`}>
                        {row.isFeatured ? "Featured" : "Not Featured"}
                      </span>
                    )}
                  </TableCell>
                )}

                {showActions && (
                  <TableCell>
                    <div className="table_actions v2">
                      <Can module="Listings Management" action="isView">
                        <Tooltip title="View" arrow>
                          <IconButton onClick={() => navigate(`/manage-vehicles/detail/${row._id}`)}>
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                        </Tooltip>
                      </Can>

                      <Can module="Listings Management" action="isEdit" isDisabledMode={true}>
                        <Tooltip title="Edit" arrow>
                          <IconButton onClick={() => navigate(`/manage-vehicles/edit/${row._id}`, { state: { vehicle: row } })}>
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                        </Tooltip>
                      </Can>

                      <Can module="Listings Management" action="isDelete" isDisabledMode={true}>
                        <Tooltip title="Delete" arrow>
                          <IconButton onClick={() => handleDeleteClick(row)}>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}

            {!isFetching && displayVehicles.length === 0 && (
              <TableRow>
                <TableCell colSpan={showActions ? 8 : 7} align="center">
                  لا توجد {type} / No {type} found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginator && paginator.pageCount > 1 && (
        <Pagination
          module={allVehicles}
          page={currentPage}
          totalPages={paginator.pageCount}
          totalCount={paginator.itemCount}
          onPageChange={(newPage) => handlePageChange(newPage)}
          setPage={handlePageChange}
        />
      )}

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete "${selectedVehicle?.name}"?`}
      />
    </>
  );
};

export default VehicleListings;
